<template>
  <section ref="fsRoot" class="maquina-estado-root" :class="{ 'fs-active': isFullscreen }">
    <!-- TOOLBAR -->
    <div class="toolbar-row">
      <div class="toolbar-left">
        <div class="toolbar-title">
          <v-icon size="16" color="#5A6476">mdi-monitor-dashboard</v-icon>
          Maqs.<template v-if="areaSeleccionadaNombre"> · {{ areaSeleccionadaNombre }}</template>
        </div>
        <div v-if="nombreTurnoCurso" class="machine-count-pill">{{ nombreTurnoCurso }}</div>
      </div>

      <!-- FILTROS DE ESTADO (chips) -->
      <div class="filter-chips toolbar-chips">
        <button
          v-for="(filtro, idx) in filtros"
          :key="'f-'+idx"
          class="filter-chip"
          :class="{ active: filtroValor === idx }"
          :style="filtroValor === idx ? activeChipStyle(filtro.opcion) : {}"
          :disabled="loading"
          @click="filtroValor = idx"
        >
          <span v-if="filtro.opcion !== null" class="chip-dot" :style="{ background: colorCard(filtro.opcion) }"></span>
          {{ filtro.nombre }}
          <span class="chip-count">{{ contarFiltro(filtro.opcion) }}</span>
        </button>
      </div>

      <div class="toolbar-actions">
        <button v-if="gamemode" class="fs-btn" @click="abrirConfig">
          <v-icon size="16" class="mr-1">mdi-cog-outline</v-icon>
          <span class="fs-btn-label">Configurar</span>
        </button>
        <button class="fs-btn" @click="extenderPantalla">
          <v-icon size="16" class="mr-1">{{ fsIcon }}</v-icon>
          <span class="fs-btn-label">{{ fsLabel }}</span>
        </button>
      </div>
    </div>

    <v-card id="projection" elevation="0" class="dashboard-card">
      <v-card-text class="dashboard-body">

        <!-- FILTROS DE AREA -->
        <div v-if="!isFullscreen" class="area-row mb-4">
          <v-autocomplete
            v-model="idArea"
            :items="areas"
            :disabled="loading"
            label="Área"
            item-value="idArea"
            item-text="nombre"
            hide-details
            clearable
            outlined
            dense
            class="area-select"
          />
          <button
            class="reload-btn"
            :disabled="loading"
            title="Recargar"
            @click="recargarManual"
          >
            <v-icon size="18" :class="{ 'reload-spin': loading }">mdi-refresh</v-icon>
          </button>
        </div>

        <!-- LOADING -->
        <v-progress-linear v-if="loading" indeterminate color="primary" rounded class="mb-4"/>

        <div v-if="loading && maquinas.length === 0" class="loading-state">
          <v-progress-circular :size="64" :width="5" color="primary" indeterminate/>
          <p class="loading-text">Cargando máquinas...</p>
        </div>

        <!-- GRID DE CARDS -->
        <v-row v-if="value && !loading || maquinas.length > 0" justify="start" align="stretch">
          <v-col
            v-for="maquina in maquinasPagina"
            :key="'maq-'+maquina.idMaquinaIns"
            :cols="12"
            :sm="dynamicCols.sm"
            :md="dynamicCols.md"
            :lg="dynamicCols.lg"
            :xl="dynamicCols.xl"
            class="d-flex"
            style="padding: 10px"
          >
            <CardMaquinaCompacta
              v-if="esCompacta"
              :item="maquina"
              :nombre-paro-prog="nombreParoProg"
              :lado-lh-rh="ladoLH_RH"
              style="width:100%"
              @detalle="abrirDetalle(maquina)"
            />
            <CardMaquina
              v-else
              :item="maquina"
              :nombre-paro-prog="nombreParoProg"
              :datos-piezas="configReporte.datosPiezas"
              style="width:100%"
              @status="cambioStatus($event, maquina)"
            />
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>

    <!-- Detalle: al hacer click en una card compacta se abre la card completa actual -->
    <v-dialog v-model="dialogDetalle" max-width="440" :content-class="detalleDialogClass">
      <CardMaquina
        v-if="maquinaDetalle"
        :item="maquinaDetalle"
        compacta
        :lado-lh-rh="ladoLH_RH"
        :nombre-paro-prog="nombreParoProg"
        :datos-piezas="configReporte.datosPiezas"
        style="width:100%"
        @status="cambioStatus($event, maquinaDetalle)"
      />
    </v-dialog>

    <!-- Configuración del reporte (solo gamemode) -->
    <v-dialog v-model="dialogConfig" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Configuración del reporte</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <label class="cfg-lbl">Versión de las tarjetas</label>
          <v-select
            v-model="versionDraft"
            :items="versionOpciones"
            :disabled="combinadoDraft"
            item-text="text"
            item-value="value"
            dense outlined hide-details
          />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            La versión <b>compacta</b> muestra tarjetas pequeñas (estilo resumido) y abre el detalle al hacer click;
            la <b>actual</b> muestra la tarjeta completa con OEE, KPIs y tiempos de ciclo.
          </p>

          <v-checkbox
            v-model="combinadoDraft"
            hide-details
            class="mt-3"
            label="Combinado (completa en normal, compacta en pantalla completa)"
          />
          <p class="mt-1 mb-0" style="color:#6b7682;font-size:12px">
            Si se activa, ignora la opción de arriba: en pantalla normal se ven las tarjetas completas y en
            pantalla completa las compactas.
          </p>

          <v-divider class="my-4" />
          <v-checkbox
            v-model="ladoLH_RHDraft"
            hide-details
            label="Agregar LH y RH"
          />
          <p class="mt-1 mb-0" style="color:#6b7682;font-size:12px">
            En el detalle de la máquina, agrega <b>LH</b> o <b>RH</b> después del código del artículo
            según el lado que indique el propio código.
          </p>

          <v-divider class="my-4" />
          <label class="cfg-lbl">Nombre de la pestaña de paros programados</label>
          <v-text-field
            v-model="nombreParoProgDraft"
            placeholder="Cambio de molde"
            maxlength="40"
            dense outlined hide-details
          />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Renombra la pestaña de estado 3 (paros programados). Por defecto es <b>Cambio de molde</b>;
            si se deja vacío vuelve a ese nombre.
          </p>

          <v-divider class="my-4" />
          <label class="cfg-lbl">Tiempo de ciclo en la tarjeta compacta</label>
          <v-select
            v-model="tcCardDraft"
            :items="tcCardOpciones"
            item-text="text"
            item-value="value"
            dense outlined hide-details
          />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Define qué tiempo de ciclo muestra la tarjeta compacta en "Tiempo ciclo".
          </p>

          <v-divider class="my-4" />
          <v-checkbox
            v-model="rotarDraft"
            hide-details
            class="mt-0"
            label="Rotar pestañas automáticamente"
          />
          <div class="d-flex align-center mt-2" style="gap:8px">
            <span style="font-size:12px;color:#5A6476">Cambiar de pestaña cada</span>
            <v-text-field
              v-model.number="rotarSegDraft"
              :disabled="!rotarDraft"
              type="number" min="3" max="600" step="1"
              dense outlined hide-details suffix="seg"
              style="max-width:120px"
            />
          </div>
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Va seleccionando cada pestaña de estado (Todas, Activas, Sin OT, Paros…) de forma cíclica.
          </p>

          <v-divider class="my-4" />
          <label class="cfg-lbl">Datos de piezas a mostrar</label>
          <div class="datos-piezas-grid">
            <v-checkbox
              v-model="datosPiezasDraft.ok"
              hide-details
              label="Pzas. Ok"
            />
            <v-checkbox
              v-model="datosPiezasDraft.noOk"
              hide-details
              label="Pzas. No OK"
            />
            <v-checkbox
              v-model="datosPiezasDraft.porOt"
              hide-details
              label="Pzas. x OT"
            />
            <v-checkbox
              v-model="datosPiezasDraft.programadas"
              hide-details
              label="Pzas. Prog."
            />
            <v-checkbox
              v-model="datosPiezasDraft.rate"
              hide-details
              label="Pzas. RATE"
            />
          </div>
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Selecciona qué columnas de datos de piezas deseas ver en la tarjeta de máquina.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="restablecerConfig">Restablecer</v-btn>
          <v-spacer />
          <v-btn text @click="dialogConfig = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="guardarConfig">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import CardMaquina from "./MaquinaEstado/CardMaquina.vue";
import CardMaquinaCompacta from "./MaquinaEstado/CardMaquinaCompacta.vue";
import vtextfieldnumber from "@/components/VTextFieldNumber.vue";
import axios from "axios";
import { mapState } from 'vuex';
import moment from 'moment';

const STATUS_COLORS = {
  1: '#2B7EFF',
  2: '#8A95A3',
  3: '#FF9F00',
  4: '#E83535',
  0: '#8A95A3',
};

export default {
  name: 'MaquinaEstado',
  components: { CardMaquina, CardMaquinaCompacta, vtextfieldnumber },

  props: {
    value:  { type: Boolean, default: true },
    pollMs: { type: Number,  default: 180000 },
  },

  data: () => ({
    sinDisponibilidad: false,
    desConParosNP: true,
    isFullscreen: false,
    _paroSnapshot: Object.create(null),

    panel: 1,
    disabled: false,
    configuracionAlerta: { oee: undefined, disponibilidad: undefined, rendimiento: undefined, calidad: undefined },

    filtroValor: 0,
    loading: false,
    maquinas: [],
    // Nombres (normalizados) de máquinas con actividad reciente según
    // /ReportesMoldes/MaquinasActivas. Sirve para distinguir las máquinas sin OT que
    // igual están activas (token físico produciendo) de las que están realmente apagadas.
    maquinasActivasApi: [],
    // Piezas sin asignar por idMaquinaIns (endpoint /ReportesMoldes/PiezasSinAsignar). Se carga
    // aparte del render principal y solo para las máquinas activas sin OT; el cache evita que las
    // cards parpadeen a "loading" en cada refresco (mantienen el último valor mientras se recalcula).
    piezasSinAsignarCache: {},
    // OT que el plan teórico dice que debería estar en curso AHORA, por idMaquinaIns (endpoint
    // /ReportesMoldes/OTProgramadaAhora). Igual que piezasSinAsignar: se pide aparte del render y
    // solo para las máquinas activas sin OT. undefined = todavía no llegó (la card muestra
    // loading); null = ya llegó y esa máquina no tiene OT programada para este momento.
    otProgramadaCache: {},

    interval: null,
    intervalCountdown: null,
    intervalPage: null,
    nextUpdateSeconds: 180,
    currentPage: 0,
    pageSizeFullscreen: 12,
    idArea: -1,
    areas: [],
    loadingAreas: false,
    idsMaquinasSeleccionadas: [],
    turnos: [],

    nombreTurnoCurso: null,
    idTurnoCurso: null,

    // Configuración del reporte (ReportesConfig, código r61). Solo editable en gamemode.
    // version: 'actual' (tarjeta completa, por defecto) | 'compacta' (tarjetas resumidas + detalle en diálogo).
    codigoReporte: 'r61',
    // version: 'actual' | 'compacta'; tcCard: qué TC muestra la card compacta; combinado: full en
    // normal + compacta en pantalla completa (ignora version); rotar/rotarSeg: rotación automática
    // entre las pestañas de estado cada N segundos; nombreParoProg: nombre de la pestaña de status 3
    // (cada planta usa el paro programado para algo distinto, por defecto es el cambio de molde).
    // ladoLH_RH: agrega el sufijo LH/RH después del código del artículo en el diálogo de detalle.
    // datosPiezas: qué columnas de piezas mostrar en la tarjeta (ok, noOk, porOt, programadas, rate)
    configReporte: {
      version: 'actual', tcCard: 'programado', combinado: false,
      rotar: false, rotarSeg: 15, nombreParoProg: 'Cambio de molde', ladoLH_RH: false,
      datosPiezas: { ok: true, noOk: true, porOt: true, programadas: true, rate: true },
    },
    versionDraft: 'actual',
    tcCardDraft: 'programado',
    combinadoDraft: false,
    rotarDraft: false,
    rotarSegDraft: 15,
    nombreParoProgDraft: 'Cambio de molde',
    ladoLH_RHDraft: false,
    datosPiezasDraft: { ok: true, noOk: true, porOt: true, programadas: true, rate: true },
    dialogConfig: false,
    intervalRotar: null,
    // Detalle: card completa dentro de un diálogo al hacer click en una card compacta.
    dialogDetalle: false,
    maquinaDetalle: null,
  }),

  watch: {
    value(n) { n ? this.iniciar() : this.detener(); },
    idArea(nuevo, previo) {
      // El area filtra en el back (idsParaConsulta), asi que solo refetcheamos si cambio de verdad.
      // -1 y null son ambos "sin area": normalizamos para evitar el doble disparo cuando Vuetify
      // convierte el -1 inicial a null al montar.
      const norm = v => (v == null ? -1 : Number(v));
      if (norm(nuevo) === norm(previo)) return;
      this.actualizarTodo();
    },
    // filtroValor NO refetchea: el filtro de estado (chips) es 100% cliente (maquinasFiltradas).
    maquinasFiltradas() {
      this.normalizarPaginaActual();
      if (this.isFullscreen && this.totalPagesFullscreen > 1) this.iniciarPaginadoFullscreen();
      if (this.totalPagesFullscreen <= 1) this.detenerPaginadoFullscreen();
    },
    isFullscreen(n) {
      this.currentPage = 0;
      n ? this.iniciarPaginadoFullscreen() : this.detenerPaginadoFullscreen();
      // La rotación de pestañas solo corre en pantalla completa: arranca al entrar, para al salir.
      this.aplicarRotacion();
    },
  },

  computed: {
    ...mapState({ nombreEmpresa: 'nombreEmpresa' }),

    // Modo desarrollador: habilita el botón/diálogo de configuración del reporte.
    gamemode() {
      return localStorage.getItem('gamemode') === 'true';
    },

    esCompacta() {
      const cfg = this.configReporte || {};
      // Combinado: tarjeta completa en normal y compacta en pantalla completa (ignora version).
      if (cfg.combinado) return this.isFullscreen;
      return cfg.version === 'compacta';
    },

    // Tipo de TC configurado que la card compacta debe mostrar.
    tcTipoCard() {
      return (this.configReporte && this.configReporte.tcCard) || 'programado';
    },

    // El diálogo de detalle se rodea con marco rojo cuando la OT en curso es crítica.
    detalleDialogClass() {
      const critica = this.maquinaDetalle && this.maquinaDetalle.otCritica;
      return critica ? 'detalle-dialog detalle-dialog--critica' : 'detalle-dialog';
    },

    // ¿Se agrega el sufijo LH/RH al código del artículo en el diálogo de detalle?
    ladoLH_RH() {
      return !!(this.configReporte && this.configReporte.ladoLH_RH);
    },

    // Nombre configurable de la pestaña de paros programados (status 3).
    nombreParoProg() {
      const nombre = (this.configReporte && this.configReporte.nombreParoProg) || '';
      return String(nombre).trim() || 'Cambio de molde';
    },

    // Pestañas de estado. Solo el nombre de la de paros programados es configurable.
    filtros() {
      return [
        { opcion: null, nombre: 'Todas'                },
        { opcion: 1,    nombre: 'Activas'              },
        { opcion: 2,    nombre: 'Sin OT'               },
        { opcion: 3,    nombre: this.nombreParoProg    },
        { opcion: 4,    nombre: 'Paros No Prog.'       },
      ];
    },

    // Nombre del área seleccionada (para mostrarlo en pantalla completa). '' si no hay filtro.
    areaSeleccionadaNombre() {
      if (!(this.idArea > 1)) return '';
      const a = (this.areas || []).find(x => Number(x.idArea) === Number(this.idArea));
      return a ? a.nombre : '';
    },

    versionOpciones() {
      return [
        { text: 'Actual (tarjeta completa)', value: 'actual' },
        { text: 'Compacta (resumida)', value: 'compacta' },
      ];
    },

    tcCardOpciones() {
      return [
        { text: 'TC Planeación', value: 'programado' },
        { text: 'TC Real', value: 'real' },
        { text: 'TC Cotizado', value: 'receta' },
      ];
    },

    fsLabel() { return this.isFullscreen ? 'Salir de Pantalla' : 'Extender Pantalla'; },
    fsIcon()  { return this.isFullscreen ? 'mdi-arrow-collapse-all' : 'mdi-arrow-expand-all'; },

    nextUpdateLabel() {
      const total = Math.max(0, Number(this.nextUpdateSeconds) || 0);
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    umbralesNorm() {
      const n = v => (v === -1 || v == null) ? null : Number(v);
      return {
        oee:           n(this.configuracionAlerta.oee),
        disponibilidad:n(this.configuracionAlerta.disponibilidad),
        rendimiento:   n(this.configuracionAlerta.rendimiento),
        calidad:       n(this.configuracionAlerta.calidad),
      };
    },

    maquinasFiltradas() {
      const opcion = this.filtros[this.filtroValor]?.opcion ?? null;
      const maquinas = this.maquinas.filter(m => {
        if (opcion === 1) return m.status === 1;
        if (opcion === 2) return m.status === 2;
        if (opcion === 3) return m.status === 3;
        if (opcion === 4) return m.status === 4;
        return true;
      });
      // Orden por nombre de máquina en TODAS las pestañas. numeric: true da el orden natural
      // que usan las plantas (100, 100A, 100B, 101, ..., 205C, 300) en vez del alfabético.
      return [...maquinas].sort((a, b) =>
        String(a.codigoMaquina || '').localeCompare(String(b.codigoMaquina || ''), 'es', { numeric: true })
      );
    },

    // Set de nombres de máquina (normalizados) con actividad reciente.
    setMaquinasActivas() {
      const set = new Set();
      (this.maquinasActivasApi || []).forEach(m => {
        const nombre = String(this.val(m, 'maquina', 'Maquina') || '').trim().toUpperCase();
        if (nombre) set.add(nombre);
      });
      return set;
    },

    // En pantalla completa se muestran TODAS las máquinas (con scroll), sin paginación ni rotación.
    maquinasPagina() {
      return this.maquinasFiltradas;
    },

    totalPagesFullscreen() {
      return 1;
    },

    dynamicCols() {
      const total = this.maquinasPagina.length;
      // Compacta: tarjetas angostas, así que caben muchas más por fila.
      if (this.esCompacta) {
        if (this.isFullscreen) return { sm: 4, md: 3, lg: 2, xl: 2 };
        return { sm: 6, md: 3, lg: 2, xl: 2 };
      }
      if (this.isFullscreen) {
        if (total <= 2)  return { sm: 12, md: 6,  lg: 6, xl: 6 };
        if (total <= 4)  return { sm: 12, md: 6,  lg: 4, xl: 3 };
        return                  { sm: 12, md: 4,  lg: 3, xl: 2 };
      }
      const filtro = this.filtros[this.filtroValor]?.opcion ?? null;
      if (filtro === null)       return { sm: 12, md: 6,  lg: 4, xl: 4 };
      if (total <= 2)            return { sm: 12, md: 6,  lg: 6, xl: 6 };
      if (total <= 4)            return { sm: 12, md: 6,  lg: 4, xl: 4 };
      return                            { sm: 12, md: 6,  lg: 4, xl: 4 };
    },
  },

  mounted() {
    this.iniciar();
    const sync = () => {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('MSFullscreenChange', sync);
    this._syncFs = sync;
  },

  beforeDestroy() {
    this.detener();
    if (this._syncFs) {
      document.removeEventListener('fullscreenchange', this._syncFs);
      document.removeEventListener('webkitfullscreenchange', this._syncFs);
      document.removeEventListener('MSFullscreenChange', this._syncFs);
    }
  },

  methods: {
    /* ────────────────────────────────────────
       FULLSCREEN
    ──────────────────────────────────────── */
    async extenderPantalla() {
      const el = this.$refs.fsRoot;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        await document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },

    /* ────────────────────────────────────────
       CONFIGURACIÓN DEL REPORTE (r61) + DETALLE
    ──────────────────────────────────────── */
    // Al hacer click en una card compacta se abre la card completa en un diálogo.
    abrirDetalle(maquina) {
      this.maquinaDetalle = maquina;
      this.dialogDetalle = true;
    },

    abrirConfig() {
      const cfg = this.configReporte || {};
      this.versionDraft = cfg.version || 'actual';
      this.tcCardDraft = cfg.tcCard || 'programado';
      this.combinadoDraft = cfg.combinado === true;
      this.rotarDraft = cfg.rotar === true;
      this.rotarSegDraft = cfg.rotarSeg || 15;
      this.nombreParoProgDraft = cfg.nombreParoProg || 'Cambio de molde';
      this.ladoLH_RHDraft = cfg.ladoLH_RH === true;
      this.datosPiezasDraft = { ...(cfg.datosPiezas || { ok: true, noOk: true, porOt: true, programadas: true, rate: true }) };
      this.dialogConfig = true;
    },

    // Rotación automática entre pestañas de estado (según config). Solo en pantalla completa.
    aplicarRotacion() {
      this.detenerRotacion();
      const cfg = this.configReporte || {};
      if (this.isFullscreen && cfg.rotar && cfg.rotarSeg > 0) {
        this.intervalRotar = setInterval(() => this.rotarPestana(), cfg.rotarSeg * 1000);
      }
    },

    rotarPestana() {
      if (!this.filtros.length) return;
      this.filtroValor = (this.filtroValor + 1) % this.filtros.length;
    },

    detenerRotacion() {
      if (this.intervalRotar) { clearInterval(this.intervalRotar); this.intervalRotar = null; }
    },

    getDefaultConfig() {
      return {
        version: 'actual', tcCard: 'programado', combinado: false,
        rotar: false, rotarSeg: 15, nombreParoProg: 'Cambio de molde', ladoLH_RH: false,
        datosPiezas: { ok: true, noOk: true, porOt: true, programadas: true, rate: true },
      };
    },

    normalizarConfig(cfg = {}) {
      const source = cfg && typeof cfg === 'object' ? cfg : {};
      const version = source.version === 'compacta' ? 'compacta' : 'actual';
      const tcCard = ['programado', 'real', 'receta'].includes(source.tcCard) ? source.tcCard : 'programado';
      const combinado = source.combinado === true;
      const rotar = source.rotar === true;
      let rotarSeg = Number(source.rotarSeg);
      if (!Number.isFinite(rotarSeg)) rotarSeg = 15;
      rotarSeg = Math.round(Math.min(600, Math.max(3, rotarSeg)));
      // Nombre de la pestaña de paros programados: vacío vuelve al default y se corta a 40 caracteres
      // para que no desborde la barra de pestañas.
      const nombreParoProg = String(source.nombreParoProg ?? '').trim().slice(0, 40) || 'Cambio de molde';
      const ladoLH_RH = source.ladoLH_RH === true;
      const datosPiezas = (source.datosPiezas && typeof source.datosPiezas === 'object')
        ? source.datosPiezas
        : { ok: true, noOk: true, porOt: true, programadas: true, rate: true };
      return { version, tcCard, combinado, rotar, rotarSeg, nombreParoProg, ladoLH_RH, datosPiezas };
    },

    // La preferencia puede llegar como string JSON, como { configJson } (camel) o { ConfigJson } (pascal).
    extraerConfig(pref = null) {
      if (pref == null) return {};
      if (typeof pref === 'string') {
        try { return JSON.parse(pref); } catch (e) { return {}; }
      }
      const rawConfigJson = pref.configJson ?? pref.ConfigJson;
      if (typeof rawConfigJson === 'string') {
        try { return JSON.parse(rawConfigJson); } catch (e) { return {}; }
      }
      return pref;
    },

    async cargarConfig() {
      // Cache local para pintar de inmediato sin esperar al backend.
      try {
        const local = localStorage.getItem(this.codigoReporte);
        if (local) this.configReporte = this.normalizarConfig(this.extraerConfig(JSON.parse(local)));
      } catch (e) { /* ignore */ }
      try {
        const { data } = await axios.get(`ReportesConfig/Obtener/${this.codigoReporte}`);
        const pref = this.normalizarConfig(this.extraerConfig(data));
        this.configReporte = pref;
        localStorage.setItem(this.codigoReporte, JSON.stringify(pref));
      } catch (e) {
        // 404 = sin config guardada aún: se usa el default ('actual'). No se persiste solo por leer.
        if (!localStorage.getItem(this.codigoReporte)) this.configReporte = this.getDefaultConfig();
      }
      this.aplicarRotacion();
    },

    async persistirPreferencias(cfg) {
      const pref = this.normalizarConfig(this.extraerConfig(cfg));
      localStorage.setItem(this.codigoReporte, JSON.stringify(pref));
      try {
        await axios.post('ReportesConfig/Guardar/', {
          codigoReporte: this.codigoReporte,
          configJson: JSON.stringify(pref),
        });
      } catch (e) { console.log(e); }
    },

    async guardarConfig() {
      this.configReporte = this.normalizarConfig({
        version: this.versionDraft,
        tcCard: this.tcCardDraft,
        combinado: this.combinadoDraft,
        rotar: this.rotarDraft,
        rotarSeg: this.rotarSegDraft,
        nombreParoProg: this.nombreParoProgDraft,
        ladoLH_RH: this.ladoLH_RHDraft,
        datosPiezas: this.datosPiezasDraft,
      });
      this.aplicarRotacion();
      await this.persistirPreferencias(this.configReporte);
      this.dialogConfig = false;
    },

    async restablecerConfig() {
      this.versionDraft = 'actual';
      this.tcCardDraft = 'programado';
      this.combinadoDraft = false;
      this.rotarDraft = false;
      this.rotarSegDraft = 15;
      this.nombreParoProgDraft = 'Cambio de molde';
      this.ladoLH_RHDraft = false;
      this.datosPiezasDraft = { ok: true, noOk: true, porOt: true, programadas: true, rate: true };
      this.configReporte = this.getDefaultConfig();
      this.aplicarRotacion();
      await this.persistirPreferencias(this.configReporte);
    },

    /* ────────────────────────────────────────
       HELPERS DE COLOR / CHIP
    ──────────────────────────────────────── */
    colorCard(status) {
      return STATUS_COLORS[status] || STATUS_COLORS[0];
    },

    activeChipStyle(opcion) {
      const c = this.colorCard(opcion ?? 0);
      return {
        background: c + '15',
        color: c,
        borderColor: c + '50',
      };
    },

    contarFiltro(opcion) {
      if (opcion === null) return this.maquinas.length;
      return this.maquinas.filter(m => m.status === opcion).length;
    },

    /* ────────────────────────────────────────
       CARGA DE DATOS (sin cambios)
    ──────────────────────────────────────── */
    toPct(v) {
      const n = Number(v);
      return Number.isFinite(n) ? (n <= 1 ? n * 100 : n) : 0;
    },

    horaActual(horas = []) {
      if (!Array.isArray(horas) || !horas.length) return null;
      const vivas = horas.filter(h => h?.pasadoOEnCurso === true);
      return vivas.length ? vivas[vivas.length - 1] : null;
    },

    slotOk(slot) {
      return (Array.isArray(slot?.cavidades) ? slot.cavidades : [])
        .reduce((acc, c) => acc + (Number(c?.piezasOk) || 0), 0);
    },

    slotNoOk(slot) {
      return (Array.isArray(slot?.cavidades) ? slot.cavidades : [])
        .reduce((acc, c) => acc + (Number(c?.piezasNoOk ?? c?.piezasNOK ?? c?.noOk ?? c?.nok ?? c?.scrap) || 0), 0);
    },

    slotActual(horas) {
      const nowMX = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Mexico_City', hour12: false, hour: '2-digit', minute: '2-digit',
      }).format(new Date());
      const [nowH, nowM] = nowMX.split(':').map(Number);
      const nowMin = nowH * 60 + nowM;
      let candidato = horas?.find(h => h?.pasadoOEnCurso === true);
      if (candidato) return candidato;
      return (horas || []).find(h => {
        const [dh, dm] = String(h.desdeHora || '00:00').split(':').map(Number);
        const [hh, hm] = String(h.hastaHora || '00:00').split(':').map(Number);
        const start = dh * 60 + dm, end = hh * 60 + hm;
        return (start <= end) ? (nowMin >= start && nowMin < end) : (nowMin >= start || nowMin < end);
      }) || null;
    },

    tieneOrdenes(ep) {
      return Array.isArray(ep?.ordenes) && ep.ordenes.length > 0;
    },

    postProcesoEstados(items) {
      if (!this._paroSnapshot || typeof this._paroSnapshot !== 'object') this._paroSnapshot = {};
      const snap = { ...this._paroSnapshot };
      const b = v => v === true || v === 1 || v === '1';
      const asArr = x => Array.isArray(x) ? x : [];
      const tieneOrdenesItem = it => asArr(it._ordenes ?? it.ordenes).length > 0;

      items.forEach(it => {
        const id = it.idMaquinaIns;
        const totalParo = Number(it._paroAcumNP || 0) + Number(it._paroAcumProg || 0);
        if (b(it._paroEnCurso)) {
          it.status = b(it._paroNPEnCurso) ? 4 : 3;
        } else if (tieneOrdenesItem(it)) {
          it.status = 1;
        } else {
          const slot = this.slotActual(asArr(it._horas));
          const produjoAhora = !!slot && (this.slotOk(slot) + this.slotNoOk(slot) > 0 || Number(slot?.piezasRateHora || 0) > 0);
          it.status = produjoAhora ? 1 : 2;
        }
        if (id != null) snap[id] = totalParo;
      });
      this._paroSnapshot = snap;
      return items;
    },

    nombreArticuloFromResumen(resumenArticulos) {
      const arts = resumenArticulos?.articulos || [];
      if (!arts.length) return '';
      if (arts.length === 1) return arts[0].nombre || '';
      return `${arts.length} artículos`;
    },

    adaptEPResumen(ep) {
      const r      = ep?.resumen || {};
      const horas  = Array.isArray(ep?.horas)   ? ep.horas   : [];
      const ordenes= Array.isArray(ep?.ordenes)  ? ep.ordenes : [];

      const codigosTodos = horas.reduce((acc, h) => {
        (Array.isArray(h?.cavidades) ? h.cavidades : []).forEach(t => {
          (Array.isArray(t?.articulos) ? t.articulos : []).forEach(a => {
            if (a?.codigo) acc.push(a.codigo);
          });
        });
        return acc;
      }, []);
      const codigosUnicos = [...new Set(codigosTodos)];

      let slot = horas.find(h => h?.pasadoOEnCurso === true) || null;
      if (!slot && horas.length) {
        const mNow = this.minutosHoyMX();
        slot = horas.find(h => {
          const [dh, dm] = String(h.desdeHora || '00:00').split(':').map(Number);
          const [hh, hm] = String(h.hastaHora || '00:00').split(':').map(Number);
          return this.rangoMinutos(mNow, dh * 60 + (dm || 0), hh * 60 + (hm || 0));
        }) || null;
      }

      const booly = v => v === true || v === 1 || v === '1';
      const enParo   = booly(r.hayParoEnCurso   ?? r.HayParoEnCurso);
      const paroProg = booly(r.hayParoProgramadoEnCurso   ?? r.HayParoProgramadoEnCurso);
      const paroNP   = booly(r.hayParoNoProgramadoEnCurso ?? r.HayParoNoProgramadoEnCurso);

      let status;
      if (enParo) {
        status = paroNP ? 4 : 3;
      } else if (slot) {
        const cavsHora = Array.isArray(slot?.cavidades) ? slot.cavidades : [];
        const piezasOkHora = cavsHora.reduce((acc, c) => acc + (Number(c?.piezasOk) || 0), 0);
        const piezasNoOkHora = cavsHora.reduce((acc, c) => acc + (Number(c?.piezasNoOk ?? c?.piezasNOK ?? c?.noOk ?? c?.nok ?? c?.scrap) || 0), 0);
        status = (piezasOkHora + piezasNoOkHora > 0 || Number(slot?.piezasRateHora || 0) > 0) ? 1 : 2;
      } else {
        const produjo = (
          Number(r?.piezasOkTotales || r?.piezasOk || 0) +
          Number(r?.piezasNoOkTotales || r?.piezasNOKTotales || r?.piezasNoOk || r?.piezasNOK || r?.noOk || r?.nok || r?.scrap || 0)
        ) > 0;
        status = produjo ? 1 : 2;
      }

      const cavRes  = r?.resumenCavidades || {};
      const cavs    = Array.isArray(cavRes.cavidades) ? cavRes.cavidades : [];
      const sumOkCav = this.firstNumericValue(
        cavRes?.piezasOkTotales,
        r?.piezasOkTotales,
        r?.piezasOk,
        cavs.reduce((a, c) => a + (Number(c?.piezasOk) || 0), 0)
      );
      const sumNokCav = this.firstNumericValue(
        cavRes?.piezasNoOkTotales,
        cavRes?.piezasNOKTotales,
        r?.piezasNoOkTotales,
        r?.piezasNOKTotales,
        r?.piezasNoOk,
        r?.piezasNOK,
        r?.noOk,
        r?.nok,
        r?.scrap,
        cavs.reduce((a, c) => a + (Number(c?.piezasNoOk ?? c?.piezasNOK ?? c?.noOk ?? c?.nok ?? c?.scrap) || 0), 0)
      );

      const tcQadSegPza = this.firstPositiveValue(
        ordenes[0]?.tiempoCicloSeg,
        ordenes[0]?.TiempoCicloSeg,
        ordenes[0]?.tiempoCiclo,
        ordenes[0]?.TiempoCiclo,
        r?.tcRecetaSeg,
        r?.TCRecetaSeg,
        r?.tiempoCicloRecetaSeg,
        r?.TiempoCicloRecetaSeg
      );
      const tcRealSegPza = this.firstPositiveValue(
        r?.tcRealSeg,
        r?.TCRealSeg,
        r?.tcReal,
        r?.TCReal,
        r?.tiempoCicloRealSeg,
        r?.TiempoCicloRealSeg,
        r?.tiempoCicloReal,
        r?.TiempoCicloReal
      );
      const segundosParoDesdeEP = paroProg
        ? Number(r.segundosTiempoParoProgramado ?? 0)
        : Number(r.segundosTiempoParo ?? 0);

      return {
        idMaquinaIns:  ep.idMaquinaIns ?? ep.id ?? null,
        codigoMaquina: r.nombreMaquina ?? r.codigoMaquina ?? ep.nombreMaquina ?? '',

        _paroEnCurso:     enParo,
        _paroProgEnCurso: paroProg,
        _paroNPEnCurso:   paroNP,
        _paroAcumNP:      Number(r.segundosTiempoParo || 0),
        _paroAcumProg:    Number(r.segundosTiempoParoProgramado || 0),
        _horas:           horas,
        _ordenes:         ordenes,

        status,
        fechaInicio: null,
        inicioParo: null,
        tiempoCambioMoldeSeg: null,
        paroTipo: null,
        paroSubTipo: null,

        oee:            this.toPct(r.oee),
        disponibilidad: this.toPct(r.disponibilidad),
        rendimiento:    this.toPct(r.rendimiento),
        calidad:        this.toPct(r.calidad),

        pizasOk:            sumOkCav,
        pizasOkPorOrden:    sumOkCav,
        planeacionPorTurno: Number(r.piezasTeoricasTotales ?? 0),
        piezasScrap:        sumNokCav,

        tiempoParoSegundos:       segundosParoDesdeEP,
        _segundosInicialesParo:   segundosParoDesdeEP,

        ot: '-',
        idMolde:      ep.idMolde      ?? r.idMolde      ?? null,
        codigoMolde:  ep.codigoMolde  ?? r.codigoMolde  ?? null,
        articulosNombres: codigosUnicos,
        tcRealSegPza,
        tcRealTxt: tcRealSegPza > 0 ? `${tcRealSegPza.toFixed(tcRealSegPza >= 10 ? 0 : 1)} seg` : null,
        tcQadSegPza,
        tcQadTxt: tcQadSegPza > 0 ? `${tcQadSegPza.toFixed(tcQadSegPza >= 10 ? 0 : 1)} seg` : null,
      };
    },

    hoyMX() {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Mexico_City',
        year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date());
    },

    rangoMinutos(m, start, end) {
      return (start <= end) ? (m >= start && m < end) : (m >= start || m < end);
    },

    minutosHoyMX() {
      const [hh, mm] = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Mexico_City', hour12: false, hour: '2-digit', minute: '2-digit',
      }).format(new Date()).split(':').map(Number);
      return hh * 60 + mm;
    },

    turnoActualId() {
      const m = this.minutosHoyMX();
      if (Array.isArray(this.turnos) && this.turnos.length) {
        for (const t of this.turnos) {
          if (this.rangoMinutos(m, this.timeToMin(t.inicio), this.timeToMin(t.fin))) return Number(t.idTurno);
        }
        const ordered = this.turnos
          .map(t => ({ id: Number(t.idTurno), start: this.timeToMin(t.inicio) }))
          .sort((a, b) => a.start - b.start);
        const last = ordered.filter(x => x.start <= m).pop() || ordered[ordered.length - 1];
        if (last) return last.id;
      }
      if (this.nombreEmpresa === 'CAZEL-TLT') {
        return this.rangoMinutos(m, 6 * 60, 18 * 60) ? 1 : 2;
      }
      return this.rangoMinutos(m, 19 * 60, 7 * 60) ? 1 : 2;
    },

    buildParams(idMaquinaIns) {
      return {
        idMaquinaIns,
        fechaRev:         this.hoyMX(),
        idTurno:          this.turnoActualId(),
        sinDisponibilidad:this.sinDisponibilidad,
        desConParosNP:    this.desConParosNP,
      };
    },

    getAreas() {
      this.loadingAreas = true;
      axios.get('/Areas')
        .then(r => { this.areas = (r.data || []).filter(a => a.idArea > 1); })
        .finally(() => { this.loadingAreas = false; });
    },

    val(obj, ...keys) {
      for (const key of keys) {
        if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
      }
      return undefined;
    },

    arr(value) {
      return Array.isArray(value) ? value : [];
    },

    bool(value) {
      return value === true || value === 1 || value === '1';
    },

    num(value, fallback = 0) {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    },

    firstNumericValue(...values) {
      for (const value of values) {
        const n = Number(value);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    },

    firstPositiveValue(...values) {
      for (const value of values) {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return 0;
    },

    formatLocalDateTime(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },

    rangoFechasTurnoCurso() {
      const today = this.hoyMX();
      const turno = this.turnos.find(t => Number(t?.idTurno) === Number(this.idTurnoCurso));

      if (!turno?.inicio || !turno?.fin) {
        const inicio = new Date(`${today}T00:00:00`);
        const fin = new Date(`${today}T23:59:59`);
        return {
          inicio: this.formatLocalDateTime(inicio),
          fin: this.formatLocalDateTime(fin),
        };
      }

      const inicioMin = this.timeToMin(turno.inicio);
      const finMin = this.timeToMin(turno.fin);
      const nowMin = this.minutosHoyMX();
      const inicio = new Date(`${today}T${turno.inicio}`);
      const fin = new Date(`${today}T${turno.fin}`);

      if (inicioMin > finMin) {
        if (nowMin < finMin) {
          inicio.setDate(inicio.getDate() - 1);
        } else {
          fin.setDate(fin.getDate() + 1);
        }
      }

      return {
        inicio: this.formatLocalDateTime(inicio),
        fin: this.formatLocalDateTime(fin),
      };
    },

    idsParaConsulta() {
      if (this.idsMaquinasSeleccionadas?.length) {
        return this.idsMaquinasSeleccionadas.filter(Boolean);
      }

      // El filtro por area lo resuelve el back via idsAreas (ver buildQueryV2), asi no
      // dependemos del catalogo pesado /MaquinaInstancias para mapear area -> IDs.
      return [];
    },

    buildQueryV2(ids, extra = {}) {
      const params = new URLSearchParams();
      ids.forEach(id => params.append('idsMaquinasIns', id));
      if (this.idArea > 1) params.append('idsAreas', this.idArea);
      Object.keys(extra).forEach(key => {
        if (extra[key] !== undefined && extra[key] !== null) params.append(key, extra[key]);
      });
      return params.toString();
    },

    async getEstadoMaquinasV2(ids) {
      const query = this.buildQueryV2(ids);
      const url = `/OEEV2/EstadoMaquinasTiempoReal${query ? `?${query}` : ''}`;
      const { data } = await axios.get(url);
      return Array.isArray(data) ? data : [];
    },

    async getMaquinasActivas() {
      try {
        const { data } = await axios.get('/ReportesMoldes/MaquinasActivas');
        return Array.isArray(data) ? data : [];
      } catch (e) {
        console.log(e);
        return [];
      }
    },

    // Consulta las piezas sin asignar (desde la última operación real) solo para las instancias
    // indicadas. Se llama al final, aparte del render, para no frenar la aparición de las cards.
    async getPiezasSinAsignar(ids) {
      const params = new URLSearchParams();
      ids.forEach(id => params.append('idsMaquinasIns', id));
      const query = params.toString();
      if (!query) return [];
      const { data } = await axios.get(`/ReportesMoldes/PiezasSinAsignar?${query}`);
      return Array.isArray(data) ? data : [];
    },

    // Rellena piezasSinAsignar de las cards activas sin OT. No bloquea el render: cada card muestra
    // su propio loading (piezasSinAsignarCargando) hasta que llega la respuesta, y el valor queda
    // cacheado por idMaquinaIns para que el siguiente refresco no parpadee.
    async cargarPiezasSinAsignar() {
      const objetivos = this.maquinas.filter(m => m.activaSinOt && m.idMaquinaIns);
      if (!objetivos.length) return;

      const ids = [...new Set(objetivos.map(m => m.idMaquinaIns))];
      try {
        const data = await this.getPiezasSinAsignar(ids);
        const mapa = {};
        data.forEach(p => {
          const id = this.val(p, 'idMaquinaIns', 'IdMaquinaIns');
          if (id != null) mapa[id] = this.num(this.val(p, 'piezasSinAsignar', 'PiezasSinAsignar'), 0);
        });
        objetivos.forEach(m => {
          const piezas = this.num(mapa[m.idMaquinaIns], 0);
          this.piezasSinAsignarCache[m.idMaquinaIns] = piezas;
          m.piezasSinAsignar = piezas;
          m.piezasSinAsignarCargando = false;
        });
      } catch (e) {
        console.log(e);
        objetivos.forEach(m => { m.piezasSinAsignarCargando = false; });
      }
    },

    // OT programada para este momento (barra teórica del plan V4 que cruza la línea de "ahora")
    // de las instancias indicadas. El back devuelve una fila por máquina que tenga plan vigente;
    // las que no tienen nada programado simplemente no vienen en la respuesta.
    async getOTProgramadaAhora(ids) {
      const params = new URLSearchParams();
      ids.forEach(id => params.append('idsMaquinasIns', id));
      const query = params.toString();
      if (!query) return [];
      const { data } = await axios.get(`/ReportesMoldes/OTProgramadaAhora?${query}`);
      return Array.isArray(data) ? data : [];
    },

    // Rellena otProgramada de las cards activas sin OT. No bloquea el render: cada card muestra su
    // propio loading (otProgramadaCargando) hasta que llega la respuesta, y el valor queda cacheado
    // por idMaquinaIns para que el siguiente refresco no parpadee.
    async cargarOtProgramada() {
      const objetivos = this.maquinas.filter(m => m.activaSinOt && m.idMaquinaIns);
      if (!objetivos.length) return;

      const ids = [...new Set(objetivos.map(m => m.idMaquinaIns))];
      try {
        const data = await this.getOTProgramadaAhora(ids);
        const mapa = {};
        data.forEach(ot => {
          const id = this.val(ot, 'idMaquinaIns', 'IdMaquinaIns');
          if (id != null) mapa[id] = ot;
        });
        objetivos.forEach(m => {
          // Sin fila en la respuesta => no hay OT programada para este momento (null, no undefined:
          // así la card deja de mostrar loading y simplemente no pinta el bloque).
          const ot = mapa[m.idMaquinaIns] || null;
          this.otProgramadaCache[m.idMaquinaIns] = ot;
          m.otProgramada = ot;
          m.otProgramadaCargando = false;
        });
      } catch (e) {
        console.log(e);
        objetivos.forEach(m => { m.otProgramadaCargando = false; });
      }
    },

    async getReporteOeeMaquinasV2(ids) {
      const rango = this.rangoFechasTurnoCurso();
      const query = this.buildQueryV2(ids, {
        inicio: rango.inicio,
        fin: rango.fin,
        calcularPorHoras: true,
      });
      const { data } = await axios.get(`/OEEV2/ReporteOEEMultiplesMaquinas?${query}`);
      return Array.isArray(data) ? data : [];
    },

    async getTiempoCicloMaquinasV2(ids) {
      const query = this.buildQueryV2(ids);
      const url = `/OEEV2/TiempoCicloMaquinas${query ? `?${query}` : ''}`;
      const { data } = await axios.get(url);
      return Array.isArray(data) ? data : [];
    },

    statusDesdeEstado(estado) {
      if (this.bool(this.val(estado, 'EnParoNoProgramado', 'enParoNoProgramado'))) return 4;
      if (this.bool(this.val(estado, 'EnParoProgramado', 'enParoProgramado'))) return 3;
      if (this.bool(this.val(estado, 'TieneParoAbierto', 'tieneParoAbierto'))) {
        const paro = this.primerParo(estado);
        const programado = this.val(paro, 'SubParoTipoProgramado', 'subParoTipoProgramado', 'ParoTipoProgramado', 'paroTipoProgramado');
        return this.bool(programado) ? 3 : 4;
      }
      if (this.bool(this.val(estado, 'TieneOperacionAbierta', 'tieneOperacionAbierta'))) return 1;
      return 2;
    },

    primeraOperacion(estado) {
      return this.arr(this.val(estado, 'OperacionesAbiertas', 'operacionesAbiertas'))[0] || {};
    },

    primerParo(estado) {
      return this.arr(this.val(estado, 'ParosAbiertos', 'parosAbiertos'))[0] || {};
    },

    oeeReporte(reporte) {
      return this.val(reporte, 'OEECalculado', 'oeeCalculado') || {};
    },

    operacionesReporte(reporte) {
      // "ODTs" es la llave del back y puede llegar como "odTs"; se resuelve sin distinguir mayúsculas.
      return this.arr(this.ciVal(reporte, 'ODTs')).reduce((acc, ot) => {
        this.arr(this.val(ot, 'Operaciones', 'operaciones')).forEach(op => acc.push(op));
        return acc;
      }, []);
    },

    firstNumberDeep(obj, keys, depth = 0) {
      if (!obj || depth > 4) return 0;
      if (Array.isArray(obj)) {
        for (const item of obj) {
          const found = this.firstNumberDeep(item, keys, depth + 1);
          if (found > 0) return found;
        }
        return 0;
      }
      if (typeof obj !== 'object') return 0;

      for (const key of Object.keys(obj)) {
        if (keys.includes(key.toLowerCase())) {
          const n = Number(obj[key]);
          if (Number.isFinite(n) && n > 0) return n;
        }
      }

      for (const key of Object.keys(obj)) {
        const found = this.firstNumberDeep(obj[key], keys, depth + 1);
        if (found > 0) return found;
      }
      return 0;
    },

    tcRecetaOperacion(operacion) {
      const minutosUnidad = this.num(this.val(operacion, 'TiempoMinXUnidad', 'tiempoMinXUnidad'), null);
      if (minutosUnidad > 0) return minutosUnidad * 60;

      const directo = this.num(this.val(
        operacion,
        'TCEsperado',
        'tcEsperado',
        'TiempoCicloSeg',
        'tiempoCicloSeg',
        'TiempoCiclo',
        'tiempoCiclo',
        'TiempoCicloSegundos',
        'tiempoCicloSegundos'
      ), 0);
      if (directo > 0) return directo;

      const minutosDeep = this.firstNumberDeep(operacion, ['tiempominxunidad']);
      if (minutosDeep > 0) return minutosDeep * 60;

      return this.firstNumberDeep(operacion, [
        'tiempocicloseg',
        'tiempociclo',
        'tiempociclosegundos',
        'tcesperado',
      ]);
    },

    tcRecetaDesdeReporte(operaciones) {
      const valores = operaciones
        .map(op => this.tcRecetaOperacion(op))
        .filter(v => Number.isFinite(v) && v > 0);
      return valores.length ? valores[0] : 0;
    },

    textoTc(segundos) {
      const n = Number(segundos);
      if (!Number.isFinite(n) || n <= 0) return null;
      return `${n.toFixed(n >= 10 ? 0 : 1)} seg`;
    },

    // Lectura de propiedad sin distinción de mayúsculas (el back tiene doble registro de
    // serializador, así que la clave puede llegar en PascalCase o camelCase).
    ciVal(obj, name) {
      if (!obj || typeof obj !== 'object') return undefined;
      const key = Object.keys(obj).find(k => k.toLowerCase() === String(name).toLowerCase());
      return key === undefined ? undefined : obj[key];
    },

    // Primera operación de la primera OT del reporte ReporteOEEMultiplesMaquinas.
    primeraOperacionReporte(reporte) {
      const primerOt = this.arr(this.ciVal(reporte, 'ODTs'))[0];
      if (!primerOt) return null;
      return this.arr(this.ciVal(primerOt, 'Operaciones'))[0] || null;
    },

    // TC Programado (seg): TiempoMinXUnidad ya viene en segundos.
    tcProgramadoReporte(reporte) {
      const primerOp = this.primeraOperacionReporte(reporte);
      if (!primerOp) return 0;
      return this.num(this.ciVal(primerOp, 'TiempoMinXUnidad'), 0);
    },

    // TC Receta (seg): TCReceta del paso/receta. Nullable en el back.
    tcRecetaReporte(reporte) {
      const primerOp = this.primeraOperacionReporte(reporte);
      if (!primerOp) return 0;
      return this.num(this.ciVal(primerOp, 'TCReceta'), 0);
    },

    // Agrupa las operaciones abiertas por artículo. Cada operación abierta cuenta como una
    // "cavidad", así que el resultado es [{ nombre, cavidades, meta }] con la cantidad de operaciones
    // (cavidades) de cada artículo distinto trabajado en la máquina y su meta de piezas.
    cavidadesPorArticulo(estado) {
      const ops = this.arr(this.val(estado, 'OperacionesAbiertas', 'operacionesAbiertas'));

      // Meta por artículo (suma de las operaciones activas de la OT), calculada en el back.
      const metaPorArticulo = new Map();
      this.arr(this.val(estado, 'Articulos', 'articulos')).forEach(a => {
        const id = this.val(a, 'IdArticulo', 'idArticulo');
        if (id == null) return;
        metaPorArticulo.set(id, this.num(this.val(a, 'PiezasProgramadas', 'piezasProgramadas'), 0));
      });

      const mapa = new Map();
      ops.forEach(op => {
        const nombre = this.val(op, 'CodigoArticulo', 'codigoArticulo') || this.val(op, 'NombreArticulo', 'nombreArticulo');
        if (!nombre) return;
        const idArticulo = this.val(op, 'IdArticulo', 'idArticulo');
        const acumulado = mapa.get(nombre) || { nombre, cavidades: 0, meta: 0 };
        acumulado.cavidades += 1;
        if (!acumulado.meta) acumulado.meta = metaPorArticulo.get(idArticulo) || 0;
        mapa.set(nombre, acumulado);
      });
      return [...mapa.values()];
    },

    adaptMaquinaV2(estado, reporte = {}, tiempoCiclo = {}, tcCargado = true) {
      const op = this.primeraOperacion(estado);
      const paro = this.primerParo(estado);
      const oee = this.oeeReporte(reporte);
      const operacionesReporte = this.operacionesReporte(reporte);
      const articulosEstado = this.arr(this.val(estado, 'Articulos', 'articulos'));
      const moldesEstado = this.arr(this.val(estado, 'Moldes', 'moldes'));
      // ciVal y no val: el back serializa "ODTs" como "odTs" (camelCase deja el acrónimo a medias),
      // así que hay que resolver la llave sin distinguir mayúsculas.
      const otsEstado = this.arr(this.ciVal(estado, 'ODTs'));

      const articulos = articulosEstado.length
        ? articulosEstado
        : operacionesReporte.map(x => ({
            CodigoArticulo: this.val(x, 'CodigoArticulo', 'codigoArticulo'),
            NombreArticulo: this.val(x, 'NombreArticulo', 'nombreArticulo'),
          }));

      const moldes = moldesEstado.length
        ? moldesEstado
        : this.arr(this.val(op, 'Moldes', 'moldes'));

      const cavidadesArticulos = this.cavidadesPorArticulo(estado);
      const totalCavidades = cavidadesArticulos.reduce((acc, c) => acc + c.cavidades, 0);

      const statusBase = this.statusDesdeEstado(estado);
      const nombreMaquina = this.val(estado, 'NombreInstancia', 'nombreInstancia') || this.val(reporte, 'NombreMaquina', 'nombreMaquina') || '';
      // Sin OT (status 2) pero con actividad reciente en MaquinasActivas: la máquina está
      // produciendo aunque nadie haya abierto una OT. Cuenta como Activa (status 1) y se
      // marca con el distintivo "Activa sin OT".
      const nombreMaquinaNorm = String(nombreMaquina).trim().toUpperCase();
      const activaSinOt = statusBase === 2 && this.setMaquinasActivas.has(nombreMaquinaNorm);
      const status = activaSinOt ? 1 : statusBase;
      const idMaquinaIns = this.val(estado, 'IdMaquinaIns', 'idMaquinaIns');
      // Piezas producidas que ninguna OT recibió, en las últimas 24 h. Ya NO llegan en este payload:
      // se cargan aparte con /ReportesMoldes/PiezasSinAsignar (ver cargarPiezasSinAsignar), solo
      // para las máquinas activas sin OT, para no bloquear el render. Mientras llega la primera
      // respuesta la card muestra loading; en refrescos reusamos el último valor cacheado.
      const piezasCache = activaSinOt ? this.piezasSinAsignarCache[idMaquinaIns] : undefined;
      const piezasSinAsignar = this.num(piezasCache, 0);
      const piezasSinAsignarCargando = activaSinOt && piezasCache === undefined;
      // OT que el plan tenía programada para este momento. Mismo esquema de cache/loading que las
      // piezas sin asignar; solo aplica a las máquinas activas sin OT.
      const otProgCache = activaSinOt ? this.otProgramadaCache[idMaquinaIns] : undefined;
      const otProgramada = otProgCache || null;
      const otProgramadaCargando = activaSinOt && otProgCache === undefined;
      const inicioParo = this.val(paro, 'InicioParo', 'inicioParo');
      const tiempoParo = this.num(this.val(paro, 'TiempoAbiertoSegundos', 'tiempoAbiertoSegundos'));
      const operacionesEstado = [
        op,
        ...this.arr(this.val(op, 'OperacionesRealesAbiertas', 'operacionesRealesAbiertas')),
      ];
      const piezasOk = this.firstNumericValue(
        this.val(oee, 'PiezasOk', 'piezasOk', 'PiezasOkTotales', 'piezasOkTotales', 'Ok', 'ok')
      );
      const piezasNoOk = this.firstNumericValue(
        this.val(
          oee,
          'PiezasNoOk',
          'piezasNoOk',
          'PiezasNOK',
          'piezasNOK',
          'PiezasNoOkTotales',
          'piezasNoOkTotales',
          'PiezasNOKTotales',
          'piezasNOKTotales',
          'NoOk',
          'noOk',
          'Nok',
          'nok',
          'Scrap',
          'scrap'
        ),
        this.firstNumberDeep(oee, ['piezasnook', 'piezasnok', 'nook', 'nok', 'scrap'])
      );
      // TC Programado: TC (seg) de la primera operación de la primera OT del reporte.
      const tcProgramadoSeg = this.tcProgramadoReporte(reporte);
      // TC Receta: TCReceta (seg) de la primera operación de la primera OT del reporte.
      const tcRecetaSeg = this.tcRecetaReporte(reporte);
      // TC Real: último ciclo medido directamente en la máquina (ultimoTC del endpoint TiempoCicloMaquinas).
      const tcRealSeg = this.num(this.val(tiempoCiclo, 'UltimoTC', 'ultimoTC'), 0);

      return {
        idMaquinaIns,
        codigoMaquina: nombreMaquina,
        nombreArea: this.val(estado, 'NombreArea', 'nombreArea') || '',
        idArea: this.val(estado, 'IdArea', 'idArea') || null,
        status,
        activaSinOt,
        piezasSinAsignar,
        piezasSinAsignarCargando,
        otProgramada,
        otProgramadaCargando,

        fechaInicio: inicioParo || this.val(op, 'Inicio', 'inicio'),
        inicioParo: inicioParo ? moment(inicioParo).format('DD/MM HH:mm') : null,
        paroTipo: this.val(paro, 'ParoTipo', 'paroTipo') || null,
        paroSubTipo: this.val(paro, 'SubParoTipo', 'subParoTipo') || null,
        tiempoParoSegundos: tiempoParo,
        _segundosInicialesParo: tiempoParo,

        oee: this.num(this.val(oee, 'OEE', 'oee'), null),
        disponibilidad: this.num(this.val(oee, 'Disponibilidad', 'disponibilidad'), null),
        rendimiento: this.num(this.val(oee, 'Rendimiento', 'rendimiento'), null),
        calidad: this.num(this.val(oee, 'Calidad', 'calidad'), null),

        pizasOk: piezasOk,
        pizasOkPorOrden: piezasOk,
        planeacionPorTurno: this.num(this.val(oee, 'PiezasEsperadas', 'piezasEsperadas')),
        piezasRate: this.num(this.val(oee, 'PiezasRateHora', 'piezasRateHora'), 0),
        piezasScrap: piezasNoOk,

        ot: this.val(otsEstado[0], 'Clave', 'clave') || this.val(op, 'Clave', 'clave') || '-',
        // Avance de la orden de trabajo: suma de Cantidad de las operaciones activas de la OT
        // (programadas) y de sus operaciones reales (hechas). Lo calcula EstadoMaquinasTiempoReal.
        piezasHechasOt: this.num(this.val(otsEstado[0], 'PiezasHechas', 'piezasHechas'), 0),
        piezasProgramadasOt: this.num(this.val(otsEstado[0], 'PiezasProgramadas', 'piezasProgramadas'), 0),
        // Totales de la OT: producidas (incluye no conformes) y no conformes.
        piezasNoOkOt: this.num(this.val(otsEstado[0], 'PiezasNoOk', 'piezasNoOk'), 0),
        // OT crítica (TrabajoOrden.OTCritica). Se toma la primera OT: una máquina no debería
        // traer más de una. Solo se usa para el marco rojo del diálogo de detalle.
        otCritica: this.bool(this.val(otsEstado[0], 'OtCritica', 'otCritica')),
        nombreArticulo: this.val(articulos[0], 'NombreArticulo', 'nombreArticulo') || '',
        articulosNombres: [...new Set(articulos
          .map(a => this.val(a, 'CodigoArticulo', 'codigoArticulo') || this.val(a, 'NombreArticulo', 'nombreArticulo'))
          .filter(Boolean))],
        idMolde: this.val(moldes[0], 'IdMolde', 'idMolde') || null,
        codigoMolde: this.val(moldes[0], 'NombreMolde', 'nombreMolde') || null,

        cavidadesArticulos,
        totalArticulos: cavidadesArticulos.length,
        totalCavidades,

        tcProgramadoSeg,
        tcProgramadoTxt: this.textoTc(tcProgramadoSeg),
        tcRealSeg,
        tcRealTxt: this.textoTc(tcRealSeg),
        tcRealCargando: !tcCargado, // true mientras no llegó TiempoCicloMaquinas (muestra loading en el chip TC Real)
        tcRecetaSeg,
        tcRecetaTxt: this.textoTc(tcRecetaSeg),

        _estadoTiempoReal: estado,
        _reporteOee: reporte,
      };
    },

    async obtenerDatos() {
      if (this.loading) return;
      this.loading = true;
      try {
        const ids = this.idsParaConsulta();

        // Solo pintamos "de a poco" en la primera carga (grid vacio). En los refrescos
        // reemplazamos de forma atomica al final para no parpadear los datos ya visibles.
        const progresivo = this.maquinas.length === 0;

        // Estado que se va acumulando conforme resuelve cada endpoint.
        let estadosRaw = null;
        let tcCargado = false; // el TC Real llega por un endpoint aparte (mas lento): mostramos loading hasta que resuelva.
        const reportesPorMaquina = new Map();
        const tiempoCicloPorMaquina = new Map();

        // Reconstruye el grid con lo que haya disponible. Requiere EstadoMaquinasTiempoReal
        // porque de ahi salen las tarjetas; los demas endpoints solo enriquecen.
        const reconstruir = () => {
          if (!progresivo || !estadosRaw) return;
          this.maquinas = estadosRaw.map(estado => {
            const id = this.val(estado, 'IdMaquinaIns', 'idMaquinaIns');
            return this.adaptMaquinaV2(estado, reportesPorMaquina.get(id) || {}, tiempoCicloPorMaquina.get(id) || {}, tcCargado);
          });
        };

        const pEstados = this.getEstadoMaquinasV2(ids).then(data => {
          estadosRaw = data;
          reconstruir();
          return data;
        });
        const pReportes = this.getReporteOeeMaquinasV2(ids).then(data => {
          data.forEach(r => reportesPorMaquina.set(this.val(r, 'IdMaquinaIns', 'idMaquinaIns'), r));
          reconstruir();
          return data;
        });
        const pActivas = this.getMaquinasActivas().then(data => {
          this.maquinasActivasApi = data;
          reconstruir(); // recalcula activaSinOt
          return data;
        });
        const pTiempos = this.getTiempoCicloMaquinasV2(ids).then(data => {
          data.forEach(t => tiempoCicloPorMaquina.set(this.val(t, 'IdMaquinaIns', 'idMaquinaIns'), t));
          tcCargado = true;
          reconstruir();
          return data;
        });

        const [estados, , activas] = await Promise.all([pEstados, pReportes, pActivas, pTiempos]);

        // Reconstruccion final atomica: garantiza consistencia y cubre el caso no-progresivo (refresco).
        this.maquinasActivasApi = activas;
        this.maquinas = estados.map(estado => {
          const id = this.val(estado, 'IdMaquinaIns', 'idMaquinaIns');
          return this.adaptMaquinaV2(estado, reportesPorMaquina.get(id) || {}, tiempoCicloPorMaquina.get(id) || {}, true);
        });
      } catch (e) { console.log(e); }
      finally { this.loading = false; }

      // Piezas sin asignar y OT programada para este momento: se consultan aparte para no retrasar
      // la aparición de las cards. Solo afectan a las máquinas activas sin OT, que muestran su
      // propio loading hasta que resuelven.
      this.cargarPiezasSinAsignar();
      this.cargarOtProgramada();
    },

    async getTurnos() {
      try {
        const { data } = await axios.get('/Turnos');
        this.turnos = Array.isArray(data) ? data : [];

        if (!this.turnos.length) {
          this.idTurnoCurso = null;
          this.nombreTurnoCurso = null;
          return;
        }

        const m = this.minutosHoyMX();
        let turnoCurso = this.turnos.find(t =>
          this.rangoMinutos(m, this.timeToMin(t?.inicio), this.timeToMin(t?.fin))
        );

        if (!turnoCurso) {
          const ordered = this.turnos
            .map(t => ({ ...t, start: this.timeToMin(t?.inicio) }))
            .sort((a, b) => a.start - b.start);
          turnoCurso = ordered.filter(t => t.start <= m).pop() || ordered[ordered.length - 1];
        }

        this.idTurnoCurso = Number(turnoCurso?.idTurno) || null;
        this.nombreTurnoCurso = turnoCurso?.nombre || null;
      } catch (e) {
        console.log(e);
        this.turnos = [];
        this.idTurnoCurso = null;
        this.nombreTurnoCurso = null;
        this.idTurnoCurso = null;
        this.nombreTurnoCurso = null;
      }
    },
    async getTurnos() {
      try {
        const { data } = await axios.get('/Turnos');
        this.turnos = Array.isArray(data) ? data : [];

        if (!this.turnos.length) {
          this.idTurnoCurso = null;
          this.nombreTurnoCurso = null;
          return;
        }

        const m = this.minutosHoyMX();
        let turnoCurso = this.turnos.find(t =>
          this.rangoMinutos(m, this.timeToMin(t?.inicio), this.timeToMin(t?.fin))
        );

        if (!turnoCurso) {
          const ordered = this.turnos
            .map(t => ({ ...t, start: this.timeToMin(t?.inicio) }))
            .sort((a, b) => a.start - b.start);
          turnoCurso = ordered.filter(t => t.start <= m).pop() || ordered[ordered.length - 1];
        }

        this.idTurnoCurso = Number(turnoCurso?.idTurno) || null;
        this.nombreTurnoCurso = turnoCurso?.nombre || null;
      } catch (e) {
        console.log(e);
        this.turnos = [];
        this.idTurnoCurso = null;
        this.nombreTurnoCurso = null;
      }
    },

    timeToMin(hms = '00:00:00') {
      const [hh = '00', mm = '00'] = String(hms).split(':');
      return Number(hh) * 60 + Number(mm);
    },

    resetCountdown() {
      this.nextUpdateSeconds = Math.max(1, Math.round(this.pollMs / 1000));
    },

    iniciarCountdown() {
      if (this.intervalCountdown) return;
      this.resetCountdown();
      this.intervalCountdown = setInterval(() => {
        this.nextUpdateSeconds = Math.max(0, this.nextUpdateSeconds - 1);
      }, 1000);
    },

    detenerCountdown() {
      if (!this.intervalCountdown) return;
      clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
    },

    normalizarPaginaActual() {
      if (this.currentPage >= this.totalPagesFullscreen) {
        this.currentPage = 0;
      }
    },

    avanzarPaginaFullscreen() {
      if (!this.isFullscreen || this.totalPagesFullscreen <= 1) return;
      this.currentPage = (this.currentPage + 1) % this.totalPagesFullscreen;
    },

    iniciarPaginadoFullscreen() {
      if (this.intervalPage || this.totalPagesFullscreen <= 1) return;
      this.intervalPage = setInterval(() => this.avanzarPaginaFullscreen(), 30000);
    },

    detenerPaginadoFullscreen() {
      if (!this.intervalPage) return;
      clearInterval(this.intervalPage);
      this.intervalPage = null;
    },

    async actualizarTodo() {
      await this.obtenerDatos();
      this.resetCountdown();
      this.normalizarPaginaActual();
      this.iniciarPaginadoFullscreen();
    },

    recargarManual() {
      if (this.loading) return;
      this.actualizarTodo();
    },

    async iniciar() {
      this.cargarConfig();
      await this.getTurnos();
      this.getAreas();
      this.getDataConfingAlert();
      await this.actualizarTodo();
      if (this.interval) return;
      this.interval = setInterval(() => this.actualizarTodo(), this.pollMs);
      this.iniciarCountdown();
    },

    detener() {
      if (this.interval) { clearInterval(this.interval); this.interval = null; }
      this.detenerCountdown();
      this.detenerPaginadoFullscreen();
      this.detenerRotacion();
    },

    cambioStatus(value, maquina) { maquina.status = value; },

    sendDataConfingAlert() {
      const num = v => isNaN(parseFloat(v)) ? null : parseFloat(v);
      axios.post('/OEEAlertas/GuardarIndicadoresOEEAlerta', {
        rendimiento:    num(this.configuracionAlerta.rendimiento),
        calidad:        num(this.configuracionAlerta.calidad),
        disponibilidad: num(this.configuracionAlerta.disponibilidad),
        oee:            num(this.configuracionAlerta.oee),
      }).catch(e => console.log(e));
    },

    getDataConfingAlert() {
      axios.get('/OEEAlertas/IndicadoresOEEAlerta').then(({ data }) => {
        if (data?.calidad        != null) this.configuracionAlerta.calidad        = data.calidad;
        if (data?.disponibilidad != null) this.configuracionAlerta.disponibilidad = data.disponibilidad;
        if (data?.oee            != null) this.configuracionAlerta.oee            = data.oee;
        if (data?.rendimiento    != null) this.configuracionAlerta.rendimiento    = data.rendimiento;
      }).catch(e => console.log(e));
    },
  },
};
</script>

<style>
/* ===================================================
   TOKENS GLOBALES (sin scoped para fullscreen)
   =================================================== */
.maquina-estado-root {
  --clr-surface: #F0F2F6;
  --clr-border:  rgba(0,0,0,0.07);
  --clr-text-primary:   #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted:     #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  background: #F4F6F9;
  transition: all 0.4s ease;
}

/* ===================================================
   DIÁLOGO DE DETALLE
   =================================================== */
/* OT crítica (TrabajoOrden.OTCritica): marco rojo alrededor de todo el diálogo. No toca la
   franja lateral de la tarjeta, que sigue representando el estado de la máquina. */
.detalle-dialog.detalle-dialog--critica {
  border: 5px solid #E83535;
  border-radius: 22px;
  box-shadow: 0 0 0 4px rgba(232, 53, 53, 0.18) !important;
  overflow: hidden;
}

/* ===================================================
   TOOLBAR
   =================================================== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  row-gap: 10px;
  padding: 16px 20px 12px;
}

/* Los chips de estado ocupan el espacio central entre el título y los botones. */
.toolbar-chips {
  flex: 1 1 auto;
  margin-bottom: 0;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cfg-lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5A6476;
  margin-bottom: 6px;
}

.datos-piezas-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 4px;
}

.datos-piezas-grid .v-checkbox {
  margin-bottom: 0;
}

.toolbar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-primary);
  font-size: 15px;
  font-weight: 600;
  color: var(--clr-text-primary);
}

.machine-count-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

.fs-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,0.15);
  background: #ffffff;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: #3d4a5c;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  white-space: nowrap;
  outline: none;
}

.fs-btn:hover {
  background: #f4f6f9;
  border-color: rgba(0,0,0,0.25);
  box-shadow: 0 2px 6px rgba(0,0,0,0.10);
}

.fs-btn:active {
  transform: scale(0.97);
}

.fs-btn .v-icon {
  color: #5A6476 !important;
}

@media (max-width: 600px) {
  .fs-btn-label { display: none; }
  .fs-btn { padding: 8px 10px; }
}

/* ===================================================
   DASHBOARD CARD
   =================================================== */
.dashboard-card.v-card {
  background: transparent !important;
  border: none !important;
}

.dashboard-body.v-card__text {
  padding: 0 12px 24px !important;
}

/* ===================================================
   ALERTS PANEL
   =================================================== */
.alerts-panel .v-expansion-panel {
  border-radius: 12px !important;
  border: 1px solid var(--clr-border) !important;
  box-shadow: none !important;
  background: #fff !important;
}

.alerts-header {
  font-family: var(--font-primary) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--clr-text-secondary) !important;
  min-height: 48px !important;
}

/* ===================================================
   AREA SELECT + RECARGA
   =================================================== */
.area-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.area-select.v-input {
  background: #fff;
  border-radius: 10px;
  font-family: var(--font-primary) !important;
  font-size: 13px !important;
  flex: 1 1 auto;
}

.reload-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--clr-border);
  background: #fff;
  color: #5A6476;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  outline: none;
}

.reload-btn:hover:not(:disabled) {
  background: #f4f6f9;
  border-color: rgba(0,0,0,0.25);
  box-shadow: 0 2px 6px rgba(0,0,0,0.10);
}

.reload-btn:active:not(:disabled) {
  transform: scale(0.96);
}

.reload-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.reload-btn .v-icon {
  color: #5A6476 !important;
}

.reload-spin {
  animation: reload-spin 0.9s linear infinite;
}

@keyframes reload-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.filter-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.filter-chip:disabled:hover {
  border-color: var(--clr-border);
  background: #fff;
}

/* ===================================================
   FILTER CHIPS
   =================================================== */
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 30px;
  border: 1.5px solid var(--clr-border);
  background: #fff;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 500;
  color: var(--clr-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.filter-chip:hover {
  border-color: rgba(0,0,0,0.18);
  background: var(--clr-surface);
}

.chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--clr-surface);
  font-size: 10px;
  font-weight: 700;
  color: var(--clr-text-muted);
  margin-left: 2px;
}

.filter-chip.active .chip-count {
  background: #fff;
  color: inherit;
  opacity: 0.95;
}

/* ===================================================
   LOADING
   =================================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 500;
  color: var(--clr-text-muted);
}

/* ===================================================
   FULLSCREEN
   =================================================== */
/* Pantalla completa: el propio panel es el contenedor de scroll (scrollea TODO el contenido).
   Se usa la clase .fs-active (basada en el flag JS isFullscreen) porque el pseudo :fullscreen
   no siempre aplica de forma fiable; el !important gana a la hoja de estilos del navegador. */
.maquina-estado-root:fullscreen,
.maquina-estado-root:-webkit-full-screen,
.maquina-estado-root.fs-active {
  padding: 14px 20px;
  height: 100vh !important;
  width: 100vw;
  background: #F4F6F9;
  overflow-y: auto !important;
  overflow-x: hidden;
}

/* El cuerpo no debe limitar ni ocultar: deja que el scroll lo maneje el panel. */
.maquina-estado-root:fullscreen .dashboard-body.v-card__text,
.maquina-estado-root:-webkit-full-screen .dashboard-body.v-card__text,
.maquina-estado-root.fs-active .dashboard-body.v-card__text {
  max-height: none !important;
  overflow: visible !important;
}

/* ===================================================
   RESPONSIVE
   =================================================== */
@media (max-width: 600px) {
  .toolbar-row   { padding: 12px 14px 8px; }
  .toolbar-title { font-size: 13px; }
  .filter-chip   { font-size: 11px; padding: 5px 10px; }
  .dashboard-body.v-card__text { padding: 0 6px 16px !important; }

  .fs-btn.v-btn .v-btn__content span:not(.v-icon) {
    display: none;
  }
}

@media (min-width: 3200px) {
  .toolbar-title  { font-size: 1.4rem; }
  .filter-chip    { font-size: 1rem; padding: 10px 20px; }
  .machine-count-pill { font-size: 0.95rem; }
}
</style>
